
import { Icon, Icons } from '@innovationnorway/component-library';
import { FC, ReactNode, useState } from 'react';

export type CustomerTaskCardProps = {
  children?: ReactNode;
  headerText: string;
  borderColor: 'blue' | 'gray' | 'red' | 'green' | 'black';
  headerColor: 'blue' | 'yellow' | 'green' | 'red' | 'gray';
  icon?: 'success' | 'info' | 'error';
  noArrow?: boolean;
};

/**
 * Base card for Customer Task cards
 */
export const CustomerTaskCardBase: FC<CustomerTaskCardProps> = (props) => {
  const getHeaderIcon = () => {
    switch (props.icon) {
      case 'info':
        return <InfoOrErrorIcon type="info" />;
      case 'success':
        return <SuccessIcon />;
      case 'error':
        return <InfoOrErrorIcon type="error" />;
      default:
        return <></>;
    }
  };

  const [showSpinner, setShowSpinner] = useState(false);

  const borderColorMap: Record<string, string> = {
    blue: 'bg-in-blue-150',
    green: 'bg-in-green-200',
    gray: 'bg-in-gray-100',
    red: 'bg-in-red-200',
    black: 'bg-in-black-50',
  };

  const headerColorMap: Record<string, string> = {
    blue: 'bg-in-blue-25 py-3',
    green: 'bg-in-green-25 py-3',
    red: 'bg-in-pink-100 py-3',
    yellow: 'bg-in-yellow-50 py-1',
    gray: 'bg-in-black-12 py-1',
  };

  return (
    <div className="p-0 rounded-none overflow-visible shadow-[0_2px_8px_rgba(0,0,0,0.25)] bg-white">
      <div className="flex w-full" onClick={() => setShowSpinner(true)}>
        <div className={`w-[3px] min-w-[3px] ${borderColorMap[props.borderColor]} rounded-none`} />
        <div className="flex-1 flex flex-col">
          <div className={`relative flex items-center w-full px-3 font-[InnovationNorway,Helvetica,Arial,sans-serif] font-normal text-base leading-[1.5] ${headerColorMap[props.headerColor]}`}
            style={{ margin: 0 }}>
            {getHeaderIcon()}
            <span className="ml-6">{props.headerText}</span>
          </div>
          <div className="flex w-full">
            <div className="p-5 overflow-hidden flex-1">
              <div>{props.children}</div>
            </div>
            {!props.noArrow && (
              <div className="flex items-center justify-end pr-5">
                {showSpinner ? (
                  <Icon size="small" name={Icons.Spinner} inline />
                ) : (
                  <Icon size="small" name={Icons.ArrowRight} inline />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// Icons
// Taken from the deprecated component in IN-component library. They are placed here as we expect the cards to get a facelift in
// the near future

const InfoOrErrorIcon = ({ type }: { type: 'info' | 'error' }) => {
  const colorMap: Record<string, string> = {
    success: 'bg-in-green-200',
    info: 'bg-[var(--color-in-blue-150)]',
    error: 'bg-[var(--color-in-red-200)]',
  };
  return (
    <div
      className={`absolute rounded-full text-white text-base h-6 w-6 text-center top-3 left-[-0.75rem] flex items-center justify-center ${type === 'info' ? colorMap['info'] : colorMap['error']}`}
    >
      <svg
        aria-hidden="true"
        focusable="false"
        data-prefix="fas"
        data-icon="info"
        className="svg-inline--fa fa-info fa-xs"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 192 512"
        color="white"
      >
        <path
          fill="currentColor"
          d="M144 80c0 26.5-21.5 48-48 48s-48-21.5-48-48s21.5-48 48-48s48 21.5 48 48zM0 224c0-17.7 14.3-32 32-32H96c17.7 0 32 14.3 32 32V448h32c17.7 0 32 14.3 32 32s-14.3 32-32 32H32c-17.7 0-32-14.3-32-32s14.3-32 32-32H64V256H32c-17.7 0-32-14.3-32-32z"
        ></path>
      </svg>
    </div>
  );
};

const SuccessIcon = () => {
  return (
    <div className="absolute rounded-full text-white text-base h-6 w-6 text-center top-3 left-[-0.75rem] flex items-center justify-center bg-in-green-200">
      <svg
        aria-hidden="true"
        focusable="false"
        data-prefix="fas"
        data-icon="check"
        className="svg-inline--fa fa-check fa-xs"
        role="img"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 512 512"
        color="white"
      >
        <path
          fill="currentColor"
          d="M470.6 105.4c12.5 12.5 12.5 32.8 0 45.3l-256 256c-12.5 12.5-32.8 12.5-45.3 0l-128-128c-12.5-12.5-12.5-32.8 0-45.3s32.8-12.5 45.3 0L192 338.7 425.4 105.4c12.5-12.5 32.8-12.5 45.3 0z"
        ></path>
      </svg>{' '}
    </div>
  );
};
